import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useE2ee } from '../lib/e2ee-context';
import { Shield } from 'lucide-react';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const { refresh } = useAuth();
  const { initE2ee } = useE2ee();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await api.post<{ ok: boolean; mfaRequired: boolean; mustChangePassword: boolean }>(
        '/auth/login',
        { email, password, remember },
      );
      await refresh();
      // E2EE-Keys mit dem Passwort initialisieren (bevor es verworfen wird)
      // Fire-and-forget — Fehler blocken nicht den Login-Flow
      initE2ee(password).catch(() => {});
      if (res.mfaRequired) nav('/2fa');
      else if (res.mustChangePassword) nav('/passwort-aendern');
      else nav('/');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Verbindungsfehler');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-rla-500 text-white mb-4">
            <Shield size={28} />
          </div>
          <h1 className="text-xl font-bold text-slate-900">RLA Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">Admin-Portal anmelden</p>
        </div>

        <form onSubmit={submit} className="card space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md px-3 py-2">
              {error}
            </div>
          )}

          <div>
            <label className="label" htmlFor="email">E-Mail</label>
            <input
              id="email"
              type="email"
              className="input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              autoComplete="email"
            />
          </div>

          <div>
            <label className="label" htmlFor="password">Passwort</label>
            <input
              id="password"
              type="password"
              className="input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
            />
          </div>

          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              className="rounded border-slate-300"
            />
            Angemeldet bleiben
          </label>

          <button type="submit" disabled={loading} className="btn-primary w-full">
            {loading ? 'Anmelden ...' : 'Anmelden'}
          </button>
        </form>
      </div>
    </div>
  );
}
